import {
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  TimeService,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { useCallback, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";
import { LazyBackDropLoading } from "../../../controls/LazyLoading";
import { ExcelButton } from "../../../controls/buttons/ExcelButton";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { isMobileDevice } from "../../../utils/responsive-utils";
import { useResize } from "../../../utils/useResize";
import type { ExportGridRef } from "../models/ProcessByShift";
import { MemoizedGanttSection } from "./MemoizedGanttSection";
import { CASProductionTable } from "./components/CASProductionTable";
import { EAFProductionTable } from "./components/EAFProductionTable";
import { LMFProductionTable } from "./components/LMFProductionTable";
import { DatePickerControl } from "./components/controls/DatePickerControl";
import type { SelectedProcess } from "./models/SelectedProcess";
import { useGanttUpdater } from "./hooks/useGanttUpdater";
import {
  useDashboardGanttQuery,
  useEAFProductionQuery,
  useLMFProductionQuery,
  useCASProductionQuery,
  useRealtimeInvalidation,
} from "./hooks/queries";
import { exportMultipleGridsToExcel } from "./utils/exportMultipleGridsToExcel";
import { exportSingleGridToExcel } from "./utils/exportSingleGridToExcel";

const moment = getMomentTz();

const SectionHeader = ({
  title,
  exportHandler,
  isDisabled,
  exportLabel,
  hideExport = false,
}: {
  title: string;
  exportHandler: () => void;
  isDisabled: boolean;
  exportLabel: string;
  hideExport?: boolean;
}) => (
  <Grid item container justifyContent="space-between" alignItems="center">
    <Grid item>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Grid>
    {!hideExport && (
      <Grid item>
        <ExcelButton
          handleExportToExcel={exportHandler}
          disabled={isDisabled}
          label={exportLabel}
        />
      </Grid>
    )}
  </Grid>
);

export const DashboardPage: React.FC = () => {
  const {
    state: { settings },
  } = useUTLSettingsContext();

  if (Object.keys(settings).length === 0) return <LazyBackDropLoading open />;

  return <DashboardPageContent />;
};

const DashboardPageContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDate = searchParams.get("date");

  const serverTz = TimeService.getInstance().getServerTimeZone();

  const now = moment.tz(serverTz);
  const initialDate = urlDate
    ? moment
        .tz(urlDate, "YYYY-MM-DD", serverTz)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        .toDate()
    : (now.hour() < 8 ? now.subtract(1, "day") : now)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        .toDate();

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [errorMessage, setErrorMessage] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedProcess, setSelectedProcess] =
    useState<SelectedProcess | null>(null);
  const [isMutationLoading, setIsMutationLoading] = useState(false);
  const { width } = useResize();
  const colWidth = (width * 0.9) / 24;

  const isMobile = isMobileDevice(width);
  const ganttHeight = !isMobile ? undefined : Math.min(width, 500);

  const [isExporting, setIsExporting] = useState(false);
  const [isExportingEAF, setIsExportingEAF] = useState(false);
  const [isExportingLMF, setIsExportingLMF] = useState(false);
  const [isExportingCAS, setIsExportingCAS] = useState(false);

  const [eafGridApi, setEafGridApi] = useState<GridApi | null>(null);
  const [lmfGridApi, setLmfGridApi] = useState<GridApi | null>(null);
  const [casGridApi, setCasGridApi] = useState<GridApi | null>(null);

  // ── Date range for queries ──
  const startDate = moment
    .tz(selectedDate, serverTz)
    .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
    .toDate();
  const endDate = moment.tz(startDate, serverTz).add(1, "days").toDate();

  // ── TanStack Queries ──
  const ganttQuery = useDashboardGanttQuery(startDate, endDate);
  const eafQuery = useEAFProductionQuery(startDate, endDate);
  const lmfQuery = useLMFProductionQuery(startDate, endDate);
  const casQuery = useCASProductionQuery(startDate, endDate);

  // ── SignalR realtime invalidation ──
  useRealtimeInvalidation();

  const handleErrorDismiss = useCallback(() => {
    setErrorMessage("");
  }, []);

  // ── Transform gantt data into Asset[] ──
  const assets = useGanttUpdater(
    selectedDate,
    ganttQuery.data?.processes,
    ganttQuery.data?.processEvents,
  );

  // ── Stable refs for MemoizedGanttSection ──
  const endCalendarDate = useMemo(
    () => moment.tz(selectedDate, serverTz).add(1, "days").toDate(),
    [selectedDate, serverTz],
  );

  // ── Derived state ──
  const EMPTY_EAF: typeof eafQuery.data = useMemo(() => [], []);
  const EMPTY_LMF: typeof lmfQuery.data = useMemo(() => [], []);
  const EMPTY_CAS: typeof casQuery.data = useMemo(() => [], []);
  const eafProcesses = eafQuery.data ?? EMPTY_EAF;
  const lmfProcesses = lmfQuery.data ?? EMPTY_LMF;
  const casProcesses = casQuery.data ?? EMPTY_CAS;
  const isLoadingBackground = ganttQuery.isFetching || isMutationLoading;

  // ── Excel export handlers ──
  const handleExcelExport = useCallback(() => {
    if (!eafGridApi || !lmfGridApi || !casGridApi) {
      setNotificationMessage("Please wait until all tables are loaded");
      return;
    }

    setIsExporting(true);
    const gridsToExport: ExportGridRef[] = [
      { gridApi: eafGridApi, sheetName: "EAF Production" },
      { gridApi: lmfGridApi, sheetName: "LMF Production" },
      { gridApi: casGridApi, sheetName: "CAS Production" },
    ];

    try {
      exportMultipleGridsToExcel(gridsToExport, selectedDate);
      setNotificationMessage("Export to Excel completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export to Excel");
    } finally {
      setIsExporting(false);
    }
  }, [eafGridApi, lmfGridApi, casGridApi, selectedDate]);

  const handleExportEAF = useCallback(() => {
    if (!eafGridApi) {
      setNotificationMessage("EAF table is not ready to export");
      return;
    }

    setIsExportingEAF(true);
    const gridRef: ExportGridRef = {
      gridApi: eafGridApi,
      sheetName: "EAF Production",
    };

    try {
      exportSingleGridToExcel(gridRef, selectedDate);
      setNotificationMessage("EAF export completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export EAF");
    } finally {
      setIsExportingEAF(false);
    }
  }, [eafGridApi, selectedDate]);

  const handleExportLMF = useCallback(() => {
    if (!lmfGridApi) {
      setNotificationMessage("Table LMF is not ready to export");
      return;
    }

    setIsExportingLMF(true);
    const gridRef: ExportGridRef = {
      gridApi: lmfGridApi,
      sheetName: "LMF Production",
    };

    try {
      exportSingleGridToExcel(gridRef, selectedDate);
      setNotificationMessage("Export of LMF completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export LMF");
    } finally {
      setIsExportingLMF(false);
    }
  }, [lmfGridApi, selectedDate]);

  const handleExportCAS = useCallback(() => {
    if (!casGridApi) {
      setNotificationMessage("Table Caster is not ready to export");
      return;
    }

    setIsExportingCAS(true);
    const gridRef: ExportGridRef = {
      gridApi: casGridApi,
      sheetName: "CAS Production",
    };

    try {
      exportSingleGridToExcel(gridRef, selectedDate);
      setNotificationMessage("Export of Caster completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export Caster");
    } finally {
      setIsExportingCAS(false);
    }
  }, [casGridApi, selectedDate]);

  const handleOnChangeDate = useCallback(
    (date: Date) => {
      const localDate = moment.tz(date, serverTz);
      const newDate = localDate
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        .toDate();

      const formattedDate = localDate.format("YYYY-MM-DD");

      setSelectedDate(newDate);
      setSearchParams({ date: formattedDate });
    },
    [setSearchParams, serverTz],
  );

  return (
    <>
      <HelmetDexteel
        title={`${selectedDate.toLocaleDateString()} - Melt Shop Dashboard`}
      />
      <Grid
        container
        spacing={2}
        sx={{ p: 2 }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Page Title */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Melt Shop Dashboard
          </Typography>
        </Grid>

        {/* Date Picker and Export Button */}
        <Grid
          item
          container
          xs={12}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={12} md="auto">
            <DatePickerControl
              value={selectedDate}
              onChange={handleOnChangeDate}
            />
          </Grid>
          {!isMobile && (
            <Grid item>
              <ExcelButton
                handleExportToExcel={handleExcelExport}
                disabled={
                  !eafGridApi ||
                  !lmfGridApi ||
                  !casGridApi ||
                  isExporting ||
                  (eafProcesses.length === 0 &&
                    lmfProcesses.length === 0 &&
                    casProcesses.length === 0)
                }
                label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
              />
            </Grid>
          )}
        </Grid>

        {/* Gantt Section */}
        <Grid item xs={12}>
          <LazyBackDropLoading
            open={isLoadingBackground || ganttQuery.isFetching}
          />

          <MemoizedGanttSection
            columnWidth={colWidth}
            assets={assets}
            showArrowTriangle={false}
            startCalendarDate={selectedDate}
            endCalendarDate={endCalendarDate}
            rowHeight={250}
            ganttHeight={ganttHeight}
            barFill={30}
            lineSectionFill={60}
            lineFill={10}
            lmfProcesses={lmfProcesses}
            ganttProcesses={ganttQuery.data?.processes}
            selectedProcess={selectedProcess}
            setSelectedProcess={setSelectedProcess}
            onError={setErrorMessage}
            onNotify={setNotificationMessage}
            setIsLoadingBackground={setIsMutationLoading}
          />
        </Grid>

        {/* EAF Production Section */}
        <Grid item xs={12}>
          <Grid container direction="column" spacing={1}>
            <SectionHeader
              title="EAF Production"
              exportHandler={handleExportEAF}
              isDisabled={
                !eafGridApi ||
                eafQuery.isLoading ||
                isExportingEAF ||
                eafProcesses.length === 0
              }
              exportLabel={isExportingEAF ? "EXPORTING..." : "EXPORT EAF"}
              hideExport={isMobile}
            />
            <Grid item xs={12}>
              <EAFProductionTable
                data={eafProcesses}
                onGridReady={(params) => {
                  setEafGridApi(params.api);
                }}
                selectedProcess={selectedProcess}
                setSelectedProcess={setSelectedProcess}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* LMF Production Section */}
        <Grid item xs={12}>
          <Grid container direction="column" spacing={1}>
            <SectionHeader
              title="LMF Production"
              exportHandler={handleExportLMF}
              isDisabled={
                !lmfGridApi ||
                lmfQuery.isLoading ||
                isExportingLMF ||
                lmfProcesses.length === 0
              }
              exportLabel={isExportingLMF ? "EXPORTING..." : "EXPORT LMF"}
              hideExport={isMobile}
            />
            <Grid item xs={12}>
              <LMFProductionTable
                data={lmfProcesses}
                onGridReady={(params) => {
                  setLmfGridApi(params.api);
                }}
                selectedProcess={selectedProcess}
                setSelectedProcess={setSelectedProcess}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* CAS Production Section */}
        <Grid item xs={12}>
          <Grid container direction="column" spacing={1}>
            <SectionHeader
              title="CAS Production"
              exportHandler={handleExportCAS}
              isDisabled={
                !casGridApi ||
                casQuery.isLoading ||
                isExportingCAS ||
                casProcesses.length === 0
              }
              exportLabel={isExportingCAS ? "EXPORTING..." : "EXPORT CAS"}
              hideExport={isMobile}
            />
            <Grid item xs={12}>
              <CASProductionTable
                data={casProcesses}
                onGridReady={(params) => {
                  setCasGridApi(params.api);
                }}
                selectedProcess={selectedProcess}
                setSelectedProcess={setSelectedProcess}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Notifications */}
      <ErrorModal error={errorMessage} onHide={handleErrorDismiss} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
